import { Button, Chip, Grid, IconButton, Paper, Tooltip, Typography, Dialog, DialogTitle, DialogContent, Drawer } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import InventoryIcon from '@mui/icons-material/Inventory2';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import NavigateBeforeOutlinedIcon from '@mui/icons-material/NavigateBeforeOutlined';
import Box from '@mui/material/Box';
import { GridColDef, GridValueGetterParams } from '@mui/x-data-grid';
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { ApiStatus, apiLimit, defaultRowsPerPageOptions } from "../../utils/api.types";
import { PermissionsEnum } from "../../utils/auth.types";
import CustomDataGrid from '../DataGrid/DataGrid';

import { fetchInventories, importInventory, downloadInventoryTemplate, exportInventory } from '../../store/inventoryStore/inventoryThunk';
import { Inventory } from '../../utils/inventory.types';
import { saveAs } from 'file-saver';
import { toast } from 'react-toastify';
import { DialogActions, CircularProgress, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

import RemoveRedEyeTwoToneIcon from '@mui/icons-material/RemoveRedEyeTwoTone';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CloseIcon from '@mui/icons-material/Close';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import BarcodeLabelDrawer, { BarcodeLabelItem } from './BarcodeLabelDrawer';
import InventoryActionMenu from '../dropdownbutton/inventoryActionMenu';
import { useNavigate } from "react-router-dom";
import { Utils } from '../../utils/common-utils';
import InventoryFilters from '../Filters/InventoryFilters';
import AuditTimeline from '../AuditTimeline/AuditTimeline';

interface InventoryList {
    accountId?: string;
    inventoryId?: string;
}
const InventoryListData: FC<InventoryList> = (props) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const { inventories, status, rowCount } = useAppSelector(state => state.inventories);
    const { permissions, selectedAccount } = useAppSelector(state => state.auth);

    const [open, setOpen] = useState(false);
    const [inventoryId, setInventory] = useState('');
    const [fetchData, setFetchData] = useState('');
    const [barcodeItem, setBarcodeItem] = useState<BarcodeLabelItem | null>(null);
    const canUpdateAccount = permissions?.includes(PermissionsEnum.Account_Update);

    const [filters, setFilters] = useState('');
    const [isRefersh, setIsRefersh] = useState(false);

    const [auditDialogOpen, setAuditDialogOpen] = useState(false);
    const [auditInventory, setAuditInventory] = useState<any>(null);

    const handleOpenAudit = (row: any) => {
        setAuditInventory(row);
        setAuditDialogOpen(true);
    };

    const handleCloseAudit = () => {
        setAuditDialogOpen(false);
        setAuditInventory(null);
    };

    const [page, setPage] = useState(0); // Current page (0-based index)
    const [pageSize, setPageSize] = useState(apiLimit.limit); // Rows per page
    const [pageQueryString, setPageQueryString] = useState(``); // Current page (0-based index)
    const [pageSizeQueryString, setPageSizeQueryString] = useState(`&limit=${apiLimit.limit}`); // Rows per page

    /**
    * Handler for account name click.
    */
    const handleEditInventoryClick = useCallback(({ _id }: Inventory) => {
        //Call edit function
        let navLink = `/inventory/edit/${_id}`;
        navigate(navLink);

    }, []);

    /**
    * Handler for New Inventory button click.
    */
    const handleAddInventory = () => {
        const navLink = `/inventory/create`;
        navigate(navLink);
    };

    // Import / export / template state + handlers.
    const [importExportAnchor, setImportExportAnchor] = useState<null | HTMLElement>(null);
    const [importOpen, setImportOpen] = useState(false);
    const [importFile, setImportFile] = useState<File | null>(null);
    const [importing, setImporting] = useState(false);
    const [importResult, setImportResult] = useState<any>(null);

    const handleDownloadTemplate = async () => {
        try {
            const blob: any = await dispatch(downloadInventoryTemplate()).unwrap();
            saveAs(blob, 'inventory_template.xlsx');
        } catch {
            toast.error('Failed to download template');
        }
    };

    const handleExport = async () => {
        try {
            const blob: any = await dispatch(exportInventory()).unwrap();
            saveAs(blob, 'inventory_export.xlsx');
        } catch {
            toast.error('Failed to export inventory');
        }
    };

    const handleImport = async () => {
        if (!importFile) return;
        setImporting(true);
        setImportResult(null);
        const res: any = await dispatch(importInventory(importFile));
        setImporting(false);
        if (res.error) {
            toast.error(res.payload?.message || 'Import failed');
            return;
        }
        setImportResult(res.payload);
        toast.success(`Import complete: ${res.payload?.created || 0} created, ${res.payload?.updated || 0} updated, ${res.payload?.skipped || 0} skipped`);
        setIsRefersh(false); // trigger list refetch
    };

    const closeImport = () => {
        setImportOpen(false);
        setImportFile(null);
        setImportResult(null);
    };

    /**
      * Fetch all the inventories on page load.
      */
    useEffect(() => {
        const { _id: id } = selectedAccount;
        const params = filters ? filters + pageQueryString + pageSizeQueryString : pageQueryString + pageSizeQueryString;
        dispatch(fetchInventories({ accountId: id, type: '', params }));
        setIsRefersh(true);
    }, [dispatch, selectedAccount, filters, isRefersh, page, pageSize, pageQueryString, pageSizeQueryString]);

    //Check status     
    const checkStatus = useCallback((value) => {
        return (value == 'Active') ? <Chip size='small' style={{ borderRadius: 7, color: '#49a563', backgroundColor: '#edf6ef' }} label={`${value}`} />
            : <Chip size='small' style={{ borderRadius: 7, color: '#c33b3b', backgroundColor: '#fbecec' }} label={`${value}`} />
    }, []);


    /** 
        * Column defs for account list data grid.
    */
    const columns: GridColDef[] = useMemo(() => [

        {
            field: "name",
            headerName: 'Name',
            type: "string",
            flex: 2,
            renderCell: ({ row, value }: GridValueGetterParams) => {
                return (
                    <div
                        onClick={() => navigate(`/inventory/view/${row._id}`)}
                        style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '2px', height: '100%', cursor: 'pointer' }}
                    >
                        <Tooltip title={`${row.name}`}>
                            <span style={{ color: '#488dff', lineHeight: 1.3 }}>
                                {(row.name.length > 30) ? `${row.name.substring(0, 30)} ...` : row.name}
                            </span>
                        </Tooltip>
                        <Tooltip title={`${row?.description || ''}`}>
                            <span style={{ color: '#464646', lineHeight: 1.3 }}>
                                {(row?.description?.length > 25) ? `${row?.description?.substring(0, 25) || ''} ...` : row?.description || ''}
                            </span>
                        </Tooltip>
                    </div>
                )
            }
        },
        {
            field: "hsnCode",
            headerName: 'HSN/SAC',
            type: "string",
            flex: 1,
            renderCell: ({ row, value }: GridValueGetterParams) => {
                return (<>{row.hsnCode}
                </>
                )
            }
        },
        {
            field: "gst",
            headerName: 'GST',
            type: "string",
            flex: 1,
            renderCell: ({ row, value }: GridValueGetterParams) => (
                <>
                    {row.gst && <>
                        @{row.gst.value || ''}%</>
                    }

                    {/* <Tooltip title={row.gst.lable}><InfoIcon style={{ fontSize: '12px', color: '#aaa' }}></InfoIcon></Tooltip> */}

                </>
            )
        },
        {
            field: "MRP",
            headerName: 'MRP',
            type: "string",
            flex: 1,
            renderCell: ({ row, value }: GridValueGetterParams) => {
                return (<>
                    {Utils.formatNumber(row.mrp || 0, true, true)}
                </>);
            },
        },
        {
            field: "purchasePrice",
            headerName: 'Pur. Price',
            type: "string",
            flex: 1,
            renderCell: ({ row, value }: GridValueGetterParams) => {
                return (<>
                    {(row.purchase && row.purchase.price && row.stock.stockAsOfDate != undefined) ?
                        <>{Utils.formatNumber(row.purchase.price || 0, true, true)}</>
                        : "-"
                    }

                </>);
            },
        },
        {
            field: "salesPrice",
            headerName: 'Sale Price',
            type: "string",
            flex: 1,
            renderCell: ({ row, value }: GridValueGetterParams) => {
                return (<>
                    {(row.sale && row.sale.price && row.sale.unit && row.sale.unit != undefined) ?
                        <>{Utils.formatNumber(row.sale.price || 0, true, true)} / {row.sale.unit.code}</>
                        : "-"
                    }

                </>);
            },
        },
        {
            field: "currentStock",
            headerName: 'In Stock',
            type: "string",
            flex: 1,
            renderCell: ({ row, value }: GridValueGetterParams) => (
                <>
                    {(row.stock && row.stock.currentStock && row.stock.currentStock > 0) ?
                        <Chip 
                            size='small'
                            style={{ borderRadius: 7, color: '#488dff', backgroundColor: '#f1f0fc' }} 
                            label={`${Utils.formatNumber(row.stock.currentStock, false)} ${(row.baseUnit && row.baseUnit.name) ?  row.baseUnit.name : ''}`} />
                        :
                        <Chip size='small' style={{ borderRadius: 7, color: '#c33b3b', backgroundColor: '#fbecec' }} label={`Out of Stock`} />
                    }
                    {/* <br/>
                {(row.stock.currentStock > 0 && row.stock.stockAsOfDate && row.stock.stockAsOfDate != '' && row.stock.stockAsOfDate != undefined) ?
                    <>{moment(row.stock.stockAsOfDate).format("DD/MM/YYYY")}</>
                    : '' 
                } */}
                </>
            )
        },
        {
            field: "status",
            headerName: 'Status',
            type: "string",
            flex: 0.8,
            renderCell: ({ row, value }: GridValueGetterParams) => (
                <>{checkStatus(value)}</>
            )
        },
        // {
        //     field: "action",
        //     headerName: 'Action',
        //     type: "string",
        //     width:130,
        //     renderCell: ({ row, value }: GridValueGetterParams) => {
        //         return (
        //             <CustomizedEstimateMenus type={props.type} row={row} refreshCompOnClose={isRefersh}/>
        //         );
        //     }
        // }

        {
            field: "actions",
            headerName: 'Actions',
            type: "actions",
            flex: 1,
            minWidth: 220,
            align: 'center',
            headerAlign: 'center',
            renderCell: ({ row, value }: GridValueGetterParams) => (
                // Plain div wrapper (not Box — the grid margin-hacks .MuiBox-root) so the
                // button centers regardless of the grid's own cell alignment classes.
                <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                    <InventoryActionMenu
                        row={row}
                        onEdit={handleEditInventoryClick}
                        onViewBarcodes={(r: any) => setBarcodeItem({
                            _id: r._id,
                            name: r.name,
                            itemCode: r.itemCode,
                            hsnCode: r.hsnCode,
                            mrp: r.mrp,
                            salePrice: r.sale?.price ?? r.salePrice,
                            unitCode: r.sale?.unit?.code,
                            baseUnit: r.baseUnit,
                            baseUnitName: r.baseUnitName,
                            unitConversions: r.unitConversions,
                            barcodeMode: r.barcodeMode,
                        })}
                        onAudit={handleOpenAudit}
                    />
                </div>
            )
            // getActions: (params: GridValueGetterParams) => {
            //     const editAction = (
            //         <GridActionsCellItem
            //             icon={<EditIcon/>}
            //             title="Edit"
            //             label="Edit"
            //             style={{marginLeft:-10, marginRight: '3px', color:'#488dff',backgroundColor:'#f1f0fc',border:'1px solid #488dff',borderRadius:10 }}
            //             onClick={() => handleEditInventoryClick(params.row)} />
            //     );

            //     const deleteAction = (
            //     <GridActionsCellItem
            //         icon={<DeleteIcon/>}
            //         title="Delete"
            //         style={{ marginRight: '0px', color:'#c33b3b',backgroundColor:'#fbecec',border:'1px solid #c33b3b',borderRadius:10 }}
            //         label="Delete"
            //         disabled
            //         // onClick={deleteSelectedAddress(params.row)}
            //     />
            //     );

            //     const actions: JSX.Element[] = [];

            //     // Push edit action if user has update permission.
            //     if (canUpdateAccount) {
            //     actions.push(editAction);
            //     }
            //     // Push edit action if user has delete permission.
            //     if (true) {
            //     actions.push(deleteAction);
            //     }
            //     return actions;
            // }
        }
    ], [
        // deleteSelected,
        // editSelected,
        //canUpdateAccount,
        // canDeleteAccount,
        //handleNameClick
    ]);

    const filterOnChangeHandler = (data: any) => {
        console.log("filter date from inventory listing ===== ", data);
        setFilters(data);
    };

    // Handle page change
    const handlePageChange = (newPage: number) => {
        setPageQueryString(`&page=${newPage}`);
        setPage(newPage);
    };

    // Handle page size change
    const handlePageSizeChange = (newPageSize: number) => {
        setPageSizeQueryString(`&limit=${newPageSize}`);
        setPageSize(newPageSize);
    };

    return (
        <Box sx={{ width: '100%' }}>
            <Box sx={{ display: "flex", flexDirection: "column", width: '100%', pt: 2, pb: 1, pr: '10px', gap: 0 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", width: '100%' }}>
                    <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
                        <InventoryFilters
                            type={'sales'}
                            filterHandler={filterOnChangeHandler} />
                    </Box>
                    <div style={{ marginLeft: 10, display: 'flex', gap: 8 }}>
                        <Button
                            endIcon={<KeyboardArrowDownIcon />}
                            size='small'
                            onClick={(e) => setImportExportAnchor(e.currentTarget)}
                            style={{ color: '#488dff', backgroundColor: '#fff', border: '0.5px solid #488dff', height: 35, borderRadius: 7 }}
                            variant="contained"
                        >
                            Import/Export
                        </Button>
                        <Menu
                            anchorEl={importExportAnchor}
                            open={Boolean(importExportAnchor)}
                            onClose={() => setImportExportAnchor(null)}
                        >
                            <MenuItem onClick={() => { setImportExportAnchor(null); handleDownloadTemplate(); }}>
                                <ListItemIcon><DescriptionOutlinedIcon fontSize="small" /></ListItemIcon>
                                <ListItemText>Download Template</ListItemText>
                            </MenuItem>
                            <MenuItem onClick={() => { setImportExportAnchor(null); handleExport(); }}>
                                <ListItemIcon><FileDownloadOutlinedIcon fontSize="small" /></ListItemIcon>
                                <ListItemText>Export Inventory</ListItemText>
                            </MenuItem>
                            <MenuItem onClick={() => { setImportExportAnchor(null); setImportOpen(true); }}>
                                <ListItemIcon><FileUploadOutlinedIcon fontSize="small" /></ListItemIcon>
                                <ListItemText>Import Inventory</ListItemText>
                            </MenuItem>
                        </Menu>
                        <Button
                            startIcon={<AddIcon />}
                            size='small'
                            onClick={handleAddInventory}
                            style={{ color: '#488dff', backgroundColor: '#fff', border: '0.5px solid #488dff', height: 35, borderRadius: 7 }}
                            variant="contained"
                        >
                            New Inventory
                        </Button>
                    </div>
                </Box>
            </Box>

            <div style={{ width: "100%", marginTop: '0px', overflow: 'auto', paddingRight: 10 }}>
            {inventories && inventories.length > 0 ? (
                <div style={{ height: "800px", width: "100%" }}>
                    <CustomDataGrid
                        columns={columns}
                        rows={inventories}
                        rowCount={rowCount}
                        rowsPerPageOptions={defaultRowsPerPageOptions}
                        page={page}
                        pageSize={pageSize}
                        onPageSizeChange={handlePageSizeChange}
                        onPageChange={handlePageChange}
                        checkboxSelection={true}
                        getRowId={(row) => row._id || Math.random().toString()}
                        loading={status === ApiStatus.LOADING} />
                </div>
            ) : status !== ApiStatus.LOADING && (
                <Paper
                    elevation={0}
                    sx={{
                        height: '70vh',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: '#fafafb',
                        border: '2px dashed #e0e0e0',
                        borderRadius: 2,
                        p: 4
                    }}
                >
                    <Box sx={{ textAlign: 'center', maxWidth: 400 }}>
                        <Box
                            sx={{
                                width: 120,
                                height: 120,
                                borderRadius: '50%',
                                backgroundColor: 'primary.light',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                margin: '0 auto',
                                mb: 3
                            }}
                        >
                            <InventoryIcon sx={{ fontSize: 60, color: 'primary.main' }} />
                        </Box>
                        <Typography
                            variant="h5"
                            sx={{ fontWeight: 600, color: '#333', mb: 1 }}
                        >
                            No Inventory Yet
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{ color: '#666', mb: 4, lineHeight: 1.6 }}
                        >
                            Start managing your inventory by adding your first item. You can track stock, manage categories, and more.
                        </Typography>
                        <Button
                            variant="contained"
                            color="primary"
                            size="large"
                            startIcon={<AddIcon />}
                            onClick={handleAddInventory}
                            sx={{
                                px: 4,
                                py: 2,
                                borderRadius: 3,
                                textTransform: 'none',
                                fontSize: '16px',
                                fontWeight: 600,
                                letterSpacing: '0.5px',
                                position: 'relative',
                                overflow: 'hidden',
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                '&:hover': {
                                    transform: 'translateY(-4px) scale(1.02)',
                                    boxShadow: (theme: any) => `0 8px 25px -8px ${theme.palette.primary.main}`,
                                },
                                '&:active': {
                                    transform: 'translateY(-2px) scale(0.98)',
                                },
                            }}
                        >
                            New Inventory
                        </Button>
                        <Typography
                            variant="caption"
                            sx={{ display: 'block', mt: 3, color: '#999' }}
                        >
                            Need help? Check our guide on inventory management
                        </Typography>
                    </Box>
                </Paper>
            )}
            </div>

            <Drawer
                anchor="right"
                open={auditDialogOpen}
                onClose={handleCloseAudit}
                PaperProps={{ sx: { width: { xs: '100%', sm: 560 }, maxWidth: '100%' } }}
            >
                <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 2, px: 3, backgroundColor: '#f5f5f5', borderBottom: '1px solid #e5e7eb' }}>
                        <AssignmentIcon sx={{ color: '#6b7280', fontSize: 26 }} />
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#111827', fontSize: '1.1rem' }}>Audit Logs</Typography>
                        </Box>
                        <IconButton size="small" onClick={handleCloseAudit}><CloseIcon /></IconButton>
                    </Box>
                    <Box sx={{ flex: 1, overflowY: 'auto', py: 2, px: 2 }}>
                        {auditInventory && (
                            <AuditTimeline entity="SKU" entityId={auditInventory._id} />
                        )}
                    </Box>
                </Box>
            </Drawer>
            <BarcodeLabelDrawer
                open={!!barcodeItem}
                onClose={() => setBarcodeItem(null)}
                accountId={selectedAccount?._id}
                item={barcodeItem}
                companyInfo={{
                    name: selectedAccount?.displayName || selectedAccount?.name,
                    phone: selectedAccount?.whatsAppNumber,
                    customerCare: selectedAccount?.whatsAppNumber || selectedAccount?.email,
                }}
            />

            <Dialog
                open={importOpen}
                onClose={closeImport}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3, boxShadow: "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)" } }}
                sx={{ "& .MuiBackdrop-root": { backgroundColor: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)" } }}
            >
                <DialogTitle sx={{ backgroundColor: "#f0fdf4", borderBottom: "1px solid #bbf7d0", display: "flex", alignItems: "center", gap: 2, py: 3, px: 3 }}>
                    <Box sx={{ backgroundColor: "#dcfce7", borderRadius: "50%", p: 1.5, display: "flex" }}>
                        <FileUploadOutlinedIcon sx={{ color: "#16a34a", fontSize: 28 }} />
                    </Box>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: "#1f2937", fontSize: "1.1rem" }}>Import Inventory</Typography>
                        <Typography variant="body2" sx={{ color: "#6b7280", mt: 0.5 }}>Upload a filled template. New items are created; existing item names are updated.</Typography>
                    </Box>
                </DialogTitle>

                <DialogContent sx={{ py: 3, px: 3 }}>
                    <Typography variant="body2" sx={{ color: "#6b7280", mt: 1, mb: 1.5 }}>
                        Download the template, fill your items, then choose the file (.xlsx / .xls / .csv).
                    </Typography>
                    <input
                        type="file"
                        accept=".xlsx,.xls,.csv"
                        onChange={(e) => { setImportFile(e.target.files?.[0] || null); setImportResult(null); }}
                    />

                    {importResult &&
                        <Box sx={{ mt: 2.5 }}>
                            <Box sx={{ display: "flex", gap: 1 }}>
                                <Chip size="small" label={`Created ${importResult.created}`} sx={{ borderRadius: 1, color: "#16a34a", backgroundColor: "#dcfce7", fontWeight: 600 }} />
                                <Chip size="small" label={`Updated ${importResult.updated || 0}`} sx={{ borderRadius: 1, color: "#2563eb", backgroundColor: "#dbeafe", fontWeight: 600 }} />
                                <Chip size="small" label={`Skipped ${importResult.skipped}`} sx={{ borderRadius: 1, color: "#b45309", backgroundColor: "#fffbeb", fontWeight: 600 }} />
                                <Chip size="small" label={`Errors ${importResult.errorCount}`} sx={{ borderRadius: 1, color: importResult.errorCount ? "#dc2626" : "#6b7280", backgroundColor: importResult.errorCount ? "#fee2e2" : "#f3f4f6", fontWeight: 600 }} />
                                <Typography variant="caption" sx={{ color: "#9ca3af", ml: "auto", alignSelf: "center" }}>of {importResult.total} rows</Typography>
                            </Box>

                            {importResult.errors?.length > 0 &&
                                <Box sx={{ mt: 2, border: "1px solid #fecaca", borderRadius: 1.5, overflow: "hidden" }}>
                                    <Box sx={{ backgroundColor: "#fef2f2", px: 2, py: 1, display: "flex", alignItems: "center", gap: 1 }}>
                                        <WarningAmberIcon sx={{ color: "#dc2626", fontSize: 18 }} />
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: "#b91c1c" }}>Failed rows ({importResult.errorCount})</Typography>
                                    </Box>
                                    <Box sx={{ maxHeight: 200, overflowY: "auto" }}>
                                        <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                                            <Box component="thead" sx={{ position: "sticky", top: 0, backgroundColor: "#f9fafb" }}>
                                                <Box component="tr">
                                                    <Box component="th" sx={{ textAlign: "left", px: 2, py: 0.75, color: "#6b7280", fontWeight: 600 }}>Row</Box>
                                                    <Box component="th" sx={{ textAlign: "left", px: 2, py: 0.75, color: "#6b7280", fontWeight: 600 }}>Item</Box>
                                                    <Box component="th" sx={{ textAlign: "left", px: 2, py: 0.75, color: "#6b7280", fontWeight: 600 }}>Reason</Box>
                                                </Box>
                                            </Box>
                                            <Box component="tbody">
                                                {importResult.errors.map((er: any, idx: number) => (
                                                    <Box component="tr" key={idx} sx={{ borderTop: "1px solid #f3f4f6" }}>
                                                        <Box component="td" sx={{ px: 2, py: 0.75, color: "#374151" }}>{er.row}</Box>
                                                        <Box component="td" sx={{ px: 2, py: 0.75, color: "#374151" }}>{er.name || <span style={{ color: "#9ca3af" }}>(blank)</span>}</Box>
                                                        <Box component="td" sx={{ px: 2, py: 0.75, color: "#dc2626" }}>{er.message}</Box>
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Box>
                                    </Box>
                                    {importResult.errorCount > importResult.errors.length &&
                                        <Typography variant="caption" sx={{ display: "block", px: 2, py: 0.75, color: "#9ca3af", backgroundColor: "#f9fafb" }}>
                                            Showing first {importResult.errors.length} of {importResult.errorCount} errors.
                                        </Typography>
                                    }
                                </Box>
                            }
                        </Box>
                    }
                </DialogContent>

                <DialogActions sx={{ backgroundColor: "#f9fafb", borderTop: "1px solid #e5e7eb", px: 3, py: 2, gap: 1.5 }}>
                    <Button onClick={handleDownloadTemplate} sx={{ textTransform: "none", color: "#374151" }} startIcon={<DescriptionOutlinedIcon />}>
                        Download Template
                    </Button>
                    <Box sx={{ flex: 1 }} />
                    <Button onClick={closeImport} variant="outlined" sx={{ minWidth: 100, height: 40, textTransform: "none", borderColor: "#d1d5db", color: "#374151" }}>
                        Close
                    </Button>
                    <Button
                        onClick={handleImport}
                        variant="contained"
                        disabled={!importFile || importing}
                        startIcon={importing ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <FileUploadOutlinedIcon sx={{ fontSize: 18 }} />}
                        sx={{ minWidth: 140, height: 40, textTransform: "none", fontWeight: 600, backgroundColor: "#16a34a", "&:hover": { backgroundColor: "#15803d" } }}
                    >
                        {importing ? 'Importing…' : 'Import'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
export default InventoryListData;
